import { Component } from '@theme/component';
import { debounce, isClickedOutside, lockScroll, onAnimationEnd, resetScrollLock, unlockScroll } from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    unlockScroll(this.refs.dialog);
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    this.#previousScrollY = getScrollTop();

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      lockScroll(dialog);

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    unlockScroll(dialog);
    scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          lockScroll(event.target);
        } else {
          unlockScroll(event.target);
        }
      }
    }
  },
  { capture: true }
);

/**
 * Mobile back navigation (the device's back gesture/button) away from a page
 * with an open dialog - Find Ring Size, Engraving, Drop a Hint, Gemologist's
 * Opinion, or any other dialog built on DialogComponent - skips closeDialog()
 * entirely, since it navigates the browser rather than interacting with the
 * page. If the browser restores that page from its back/forward cache
 * (bfcache) - e.g. the shopper navigates forward again, or their "back"
 * gesture is itself a bfcache restore of a previous entry - the entire page
 * comes back exactly as it was frozen: this module's scroll-lock owner(s)
 * still recorded, the shared `[scroll-lock]` attribute still applied to
 * <html>, and the dialog element still flagged open. Some mobile browsers
 * don't reliably restore a native <dialog> to its visible "open" top-layer
 * state after a bfcache restore, so the shopper lands on a page that looks
 * like the dialog is gone - but scrolling is permanently locked, with no
 * visible control left to close it and undo the lock.
 *
 * `pageshow` fires on every page display, including ordinary loads (where
 * this is a harmless no-op, since nothing is open yet) and bfcache restores
 * (where it matters). Forcibly closing any dialog still marked open and
 * hard-resetting the scroll lock here - rather than only on a "real" close -
 * guarantees the shopper can always scroll again after using the
 * device/browser back control instead of a dialog's own close (X) button.
 */
window.addEventListener('pageshow', () => {
  /*
   * Grauv fix: side drawers (<theme-drawer>: the cart drawer, the chat drawer)
   * are NOT ordinary dialogs. On desktop they are non-modal sidebars that
   * squeeze the page to make room (the page gets a margin-right and the
   * `page-wrapper--drawer-open` class), and the theme deliberately re-opens
   * them after navigation (see the sessionStorage restore script in
   * snippets/theme-drawer.liquid).
   *
   * The blanket dialog.close() below used to close the drawer's <dialog>
   * on every pageshow without telling the drawer, so after
   * Cart drawer -> Checkout -> browser Back the drawer was gone but the page
   * was still squeezed - leaving a blank strip down the right side.
   *
   * So: on wide screens leave open drawers alone (they stay open and the page
   * layout stays consistent). On narrow screens (modal drawers, where the
   * restore problem described above can really happen) close them properly
   * and sync the drawer's own state, the squeeze class and the saved state.
   */
  const isNarrow = window.matchMedia('(max-width: 989px)').matches;
  /** @type {Set<Element>} */
  const drawersToSync = new Set();

  document.querySelectorAll('dialog[open]').forEach((dialog) => {
    const drawer = dialog.closest('theme-drawer');

    if (drawer) {
      if (!isNarrow) return;
      drawersToSync.add(drawer);
    }

    dialog.close();
  });

  drawersToSync.forEach((drawer) => {
    drawer.removeAttribute('open');
    drawer.style.removeProperty('--drawer-stack-order');
    // Same event ThemeDrawer fires on close: removes the page squeeze,
    // updates the header buttons and clears the persisted open-drawer state.
    drawer.dispatchEvent(new CustomEvent('theme-drawer:close', { bubbles: true }));
  });

  resetScrollLock();
});
